import { PersonalInfo, WorkRole, CaseStudy, Article, MediaAppearance, SpeakingEngagement, TrustedBy } from './types';

export const personalInfo: PersonalInfo = {
  name: "Andreas Lutz",
  tagline: "Swiss Stablecoin & Digital Asset Infrastructure Executive",
  bio: "I work on Swiss stablecoin infrastructure: the operating models, payments, treasury and governance that let CHF digital currency function in regulated and real-world settings. I have 11 years of experience in digital assets, at the intersection of operations, product and commercial execution.\n\nI currently advise Swiss Stablecoin on platform operations for CHFD, a regulated Swiss franc stablecoin, and serve as Chief Operating Officer at Liberty Gaming, leading operations, treasury processes, go-to-market execution and launch preparation for a blockchain gaming ecosystem. Previously I was Head of Operations at the Frankencoin Association (2024–2025), where I led operational build-out and Switzerland's SPAR merchant rollout across 360 stores.\n\nEarlier in my career I co-founded NetworkLearning, served as Operations Manager at Forflies Academy, helped scale operations at Funnalex from five-digit ARR to the mid-seven figures, and advised scaleups across EMEA at PwC. I hold a Master of Science in Finance from Aalto University and a Bachelor's degree in Accounting and Finance from Haaga-Helia University of Applied Sciences.",
  highlights: [
    "11+ years in digital assets, stablecoins and operations, based in Switzerland",
    "Operations Consultant for <a href='https://swissstablecoin.ch/' target='_blank' rel='noopener noreferrer' class='text-accent-crimson hover:text-accent-crimson/80 underline'>Swiss Stablecoin</a> (CHFD) - <a href='#case-study-1' class='text-accent-crimson hover:text-accent-crimson/80 underline'>platform operations</a> for regulated Swiss franc infrastructure",
    "Former Head of Operations at the <a href='https://frankencoin.com' target='_blank' rel='noopener noreferrer' class='text-accent-crimson hover:text-accent-crimson/80 underline'>Frankencoin</a> Association (2024–2025) - <a href='#case-study-2' class='text-accent-crimson hover:text-accent-crimson/80 underline'>operational build-out</a>, mint/redeem, treasury and SPAR merchant adoption",
    "Chair of the <a href='https://oraclefreedollar.org/' target='_blank' rel='noopener noreferrer' class='text-accent-crimson hover:text-accent-crimson/80 underline'>OFD Association</a> (USD stablecoin) - <a href='#case-study-3' class='text-accent-crimson hover:text-accent-crimson/80 underline'>governance and adoption</a>",
    "Chief Operating Officer at Liberty Gaming - operations, treasury, go-to-market and launch preparation for a blockchain gaming ecosystem",
    "Board member of early-stage <a href='https://stablepln.pl' target='_blank' rel='noopener noreferrer' class='text-accent-crimson hover:text-accent-crimson/80 underline'>PLNC</a> (PLN stablecoin); advisor to NEXI Chain (AED stablecoin)",
    "Officially recognized as <a href='https://duckbucks.com/experts' target='_blank' rel='noopener noreferrer' class='text-accent-crimson hover:text-accent-crimson/80 underline'>cryptocurrency ecosystem expert</a> by H.S.H. Prince Michael of Liechtenstein",
    "Co-founded blockchain education platform NetworkLearning in 2016 (8,500+ users); Operations Manager at <a href='https://forfliescrypto.com/' target='_blank' rel='noopener noreferrer' class='text-accent-crimson hover:text-accent-crimson/80 underline'>Forflies Academy</a>",
    "Published in <a href='https://duckbucks.com/a/frankencoin-decentralized-stablecoin-blockchain-innovation' target='_blank' rel='noopener noreferrer' class='text-accent-crimson hover:text-accent-crimson/80 underline'>finance magazines</a> and <a href='https://block24seven.com/reimagining-stablecoins-with-oracle-free-dollar-ofd/' target='_blank' rel='noopener noreferrer' class='text-accent-crimson hover:text-accent-crimson/80 underline'>news outlets</a>",
    "Guest lecturer at <a href='https://www.haaga-helia.fi/en' target='_blank' rel='noopener noreferrer' class='text-accent-crimson hover:text-accent-crimson/80 underline'>Haaga-Helia University of Applied Sciences</a> (Helsinki); advised scaleups across EMEA at <a href='https://www.pwc.com/gx/en/services/entrepreneurial-private-business/emea-private-business/our-services-for-investors.html' target='_blank' rel='noopener noreferrer' class='text-accent-crimson hover:text-accent-crimson/80 underline'>PwC</a>"
  ],
  focusAreas: [
    "Swiss stablecoins",
    "Digital asset infrastructure",
    "Institutional payments",
    "Corporate treasury",
    "Operations and execution",
    "Swiss financial infrastructure"
  ],
  email: "hello@andreas-lutz.ch",
  social: {
    linkedin: "https://www.linkedin.com/in/andreas-lutz"
    // twitter: "https://twitter.com/andreas_lutz", // Ready to uncomment when needed
    // github: "https://github.com/andreas-lutz" // Ready to uncomment when needed
  }
};

export const workRoles: WorkRole[] = [
  {
    id: "1",
    title: "Senior Stablecoin Strategist",
    organization: "Crypto Finance AG",
    timeSpan: "2022 - Present",
    outcomes: [
      "Led development of $500M+ stablecoin infrastructure",
      "Established regulatory compliance across 8 jurisdictions",
      "Reduced transaction costs by 40% through optimization",
      "Built strategic partnerships with 15+ financial institutions"
    ],
    badges: ["Stablecoins", "Payments", "Compliance"],
    caseStudyUrl: "#case-study-1"
  },
  {
    id: "2",
    title: "Head of Crypto Strategy",
    organization: "Fintech Innovations Ltd",
    timeSpan: "2020 - 2022",
    outcomes: [
      "Developed crypto payment solutions for 100K+ users",
      "Launched cross-border remittance platform",
      "Achieved 99.9% uptime for payment processing",
      "Secured $25M in Series B funding"
    ],
    badges: ["Strategy", "Payments", "Fundraising"],
    caseStudyUrl: "#case-study-2"
  },
  {
    id: "3",
    title: "Blockchain Consultant",
    organization: "Digital Assets Consulting",
    timeSpan: "2018 - 2020",
    outcomes: [
      "Advised 20+ companies on blockchain implementation",
      "Developed tokenomics models for 5 major projects",
      "Established regulatory frameworks in 3 countries",
      "Generated $10M+ in client value"
    ],
    badges: ["Consulting", "Tokenomics", "Regulation"],
    caseStudyUrl: "#case-study-3"
  }
];

export const caseStudies: CaseStudy[] = [
  {
    id: "1",
    project: "Swiss Stablecoin (CHFD)",
    role: "Operations Consultant",
    problem: "Switzerland needs regulated digital franc infrastructure that can sit alongside existing payments, treasury and financial-market processes.",
    solution: "As Operations Consultant I advise Swiss Stablecoin on platform operations: helping establish the operational framework, governance model and processes for CHFD, a regulated Swiss franc stablecoin built for institutional and real-economy use.",
    impact: [
      { metric: "Operational framework", value: "for regulated CHF infrastructure" },
      { metric: "Governance and processes", value: "for platform operations" },
      { metric: "Institutional alignment", value: "with Swiss financial market requirements" }
    ],
    details: "Swiss Stablecoin is developing CHFD as a regulated, CHF-backed digital franc. My role is operational and advisory: platform operations, operating model and process design. I am not the issuer, and I did not found the company."
  },
  {
    id: "2",
    project: "Frankencoin (ZCHF)",
    role: "Head of Operations, 2024–2025",
    problem: "A CHF stablecoin only becomes useful if issuance, redemption, treasury and merchant acceptance actually work in the real economy, not only in DeFi.",
    solution: "As Head of Operations at the Frankencoin Association I led operational build-out and market adoption: issuance operations, mint and redeem processes, distribution, treasury coordination, merchant adoption, and the SPAR payment rollout across 360 stores in Switzerland. I was responsible for operational leadership, not for developing the protocol.",
    impact: [
      { metric: "SPAR merchant rollout", value: "360 stores across Switzerland" },
      { metric: "Mint, redeem and treasury", value: "operational processes designed" },
      { metric: "Ecosystem products", value: "launch support and execution" }
    ],
    details: "Frankencoin is a decentralized Swiss franc stablecoin. My mandate was to make the association's operations and real-world adoption work: payments, treasury coordination, distribution and product launch execution. Protocol design was not my role."
  },
  {
    id: "3",
    project: "Oracle-Free Dollar (OFD)",
    role: "Chairman of the Board",
    problem: "Most dollar stablecoins still depend on external oracles and have few uses that reach people outside crypto-native markets.",
    solution: "As Chairman of the OFD Association I am responsible for governance and adoption strategy, including the integration of OFD as in-game currency with SIXR, a global cricket game.",
    impact: [
      { metric: "Governance framework", value: "established" },
      { metric: "Adoption strategy", value: "designed" },
      { metric: "SIXR integration", value: "in-game currency use case" }
    ],
    details: "OFD is a dollar stablecoin that follows an oracle-free design, reducing reliance on external price feeds. My role is governance and adoption, not protocol engineering."
  },
  {
    id: "4",
    project: "Polish Zloty Stablecoin (PLNC)",
    role: "Board Member",
    problem: "There are few trusted, regulatory-compatible PLN stablecoin options for users and institutions in Poland and the wider EU.",
    solution: "As a board member I support the early-stage PLNC initiative on governance, direction and the path toward a regulatory-compatible zloty stablecoin.",
    impact: [
      { metric: "Board-level governance", value: "for an early-stage PLN stablecoin" },
      { metric: "Regulatory direction", value: "toward EU-compatible design" }
    ],
    details: "PLNC is an early-stage Polish zloty stablecoin initiative. My role is board-level, not day-to-day operational leadership and not protocol development."
  },
  {
    id: "5",
    project: "NEXI Chain (AED)",
    role: "Advisor",
    problem: "An AED-pegged stablecoin still needs practical work on model design, governance and go-to-market.",
    solution: "I advise NEXI Chain on stablecoin model design, governance and go-to-market as a member of the advisory board.",
    impact: [
      { metric: "Advisory Board", value: "member" },
      { metric: "Model, governance, GTM", value: "advisory support" }
    ],
    details: "This is an advisory mandate for an AED-pegged stablecoin. I do not run the project and I did not build the underlying chain."
  }
];

export const articles: Article[] = [
  {
    id: "1",
    title: "Reimagining Stablecoins",
    publication: "Block24Seven",
    date: "2025-09-18",
    url: "https://block24seven.com/reimagining-stablecoins-with-oracle-free-dollar-ofd/",
    tags: ["Stablecoins", "Oracle-Free", "Innovation"]
  },
  {
    id: "2",
    title: "Toward a More Perfect Stablecoin",
    publication: "DuckBucks",
    date: "2025-01-07",
    url: "https://duckbucks.com/a/frankencoin-decentralized-stablecoin-blockchain-innovation",
    tags: ["Frankencoin", "Decentralization", "Blockchain"]
  },
  {
    id: "3",
    title: "What Does Oracle-Free Really Mean?",
    publication: "Substack",
    date: "2024-12-15",
    url: "https://substack.com/@andreasfrankencoin/p-149975857",
    tags: ["Oracle-Free", "Technical", "Education"]
  }
];

export const mediaAppearances: MediaAppearance[] = [
  {
    id: "1",
    outlet: "CoinDesk",
    title: "The Evolution of Stablecoin Regulation",
    date: "2024-02-10",
    url: "https://example.com/coindesk-interview",
    type: "Interview",
    logoUrl: "/logos/coindesk.svg"
  },
  {
    id: "2",
    outlet: "Fintech Summit 2024",
    title: "Panel: Future of Digital Payments",
    date: "2024-01-25",
    url: "https://example.com/fintech-panel",
    type: "Panel",
    logoUrl: "/logos/fintech-summit.svg"
  },
  {
    id: "3",
    outlet: "Crypto Conference",
    title: "Keynote: Building Trust in Digital Assets",
    date: "2023-12-15",
    url: "https://example.com/crypto-keynote",
    type: "Keynote",
    logoUrl: "/logos/crypto-conference.svg"
  }
];

export const speakingEngagements: SpeakingEngagement[] = [
  {
    id: "1",
    event: "Fintech Summit 2024",
    city: "London",
    title: "The Future of Stablecoin Infrastructure",
    date: "2024-03-15",
    slidesUrl: "https://example.com/slides1",
    videoUrl: "https://example.com/video1"
  },
  {
    id: "2",
    event: "Crypto Regulation Conference",
    city: "Singapore",
    title: "Multi-Jurisdictional Compliance Strategies",
    date: "2024-02-20",
    slidesUrl: "https://example.com/slides2"
  },
  {
    id: "3",
    event: "Blockchain Innovation Forum",
    city: "New York",
    title: "Building Sustainable Tokenomics",
    date: "2023-11-10",
    slidesUrl: "https://example.com/slides3",
    videoUrl: "https://example.com/video3"
  }
];

export const trustedBy: TrustedBy[] = [
  {
    id: "1",
    name: "Frankencoin",
    logoUrl: "/coin_logo_frankencoin.svg",
    url: "https://frankencoin.com"
  },
  {
    id: "2",
    name: "Oracle Free Dollar",
    logoUrl: "/OFD Official Logo (1).jpg",
    url: "https://oraclefreedollar.com"
  },
  {
    id: "7",
    name: "SIXR",
    logoUrl: "/SIXR logo no bg.png",
    url: "https://sixrcricket.com/"
  },
  {
    id: "3",
    name: "Haaga-Helia",
    logoUrl: "/hh logo.png",
    url: "https://www.haaga-helia.fi/en"
  },
  {
    id: "4",
    name: "DuckBucks",
    logoUrl: "/duckbucks logo.png",
    url: "https://duckbucks.com/experts"
  },
  {
    id: "5",
    name: "Nexi Chain",
    logoUrl: "/nexi chain.png",
    url: "https://nexi.technology/"
  },
  {
    id: "6",
    name: "Block24Seven",
    logoUrl: "/bloch24.jpeg",
    url: "https://block24seven.com/reimagining-stablecoins-with-oracle-free-dollar-ofd/"
  }
];
